import { useState } from 'react'
import {
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
  ListItemIcon,
  useScrollTrigger,
} from '@mui/material'
import {
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useAuth } from '../../../context/AuthContext'
import { useBrowseHistory } from '../../../context/browse-history-context'
import { useDebouncedSearch } from '../../../hooks/useDebouncedSearch'
import MenuIcon from '@mui/icons-material/Menu'
import {
  StyledAppBar,
  StyledToolbar,
  RightContainer,
  LogoWrapper,
  LogoBox,
  LogoText,
  LogoAccent,
  NavButton,
  MobileMenuArea,
  MenuIconButton,
  MobileLogoOverlay,
  MobileLogoClickable,
  DesktopLeftContainer,
  DesktopNavContainer,
  DesktopSignInButton,
  DesktopLoggedOutArea,
  DesktopMoreMenuButton,
  SearchWrapper,
  SearchPanel,
  SearchRow,
  SearchInput,
  CloseButton,
  ResultsBox,
  ResultItem,
  SearchThumb,
  SearchThumbFallback,
  StatusRow,
  SearchIconButton,
  UserAvatar,
} from './header.styles'
import PersonIcon from '@mui/icons-material/Person'
import SettingsIcon from '@mui/icons-material/Settings'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined'
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined'
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined'
import LoginIcon from '@mui/icons-material/Login'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LazyImage from '../../../components/lazy-image'

export default function Header() {
  const navigate = useNavigate()
  const { isLoading, isAuthenticated, user, loginWithPopup, logout } = useAuth()
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 60 })

  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [moviesAnchor, setMoviesAnchor] = useState<null | HTMLElement>(null)
  const [showsAnchor, setShowsAnchor] = useState<null | HTMLElement>(null)
  const [actorsAnchor, setActorsAnchor] = useState<null | HTMLElement>(null)
  const [avatarAnchor, setAvatarAnchor] = useState<null | HTMLElement>(null)
  const [desktopMoreAnchor, setDesktopMoreAnchor] =
    useState<null | HTMLElement>(null)
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null,
  )
  const [drawerScreen, setDrawerScreen] = useState<
    'root' | 'movies' | 'shows' | 'actors'
  >('root')
  const [submenuAnchor, setSubmenuAnchor] = useState<null | HTMLElement>(null)
  const [submenuType, setSubmenuType] = useState<
    'movies' | 'shows' | 'actors' | null
  >(null)

  const { results, loading } = useDebouncedSearch(searchQuery, 400)
  const { recentViews } = useBrowseHistory()

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
      setSearchOpen(false)
      setSearchQuery('')
    }
  }

  const navItems = [
    { label: 'Discover', path: '/discover' },
    { label: 'Randomizer', path: '/randomizer' },
  ]

  const moviesDropdown = [
    { label: 'Popular', path: '/movies/popular' },
    { label: 'Top Rated', path: '/movies/top-rated' },
    { label: 'Now Playing', path: '/movies/now-playing' },
    { label: 'Upcoming', path: '/movies/upcoming' },
    { label: 'Marvel', path: '/movies/marvel' },
    { label: 'Netflix', path: '/movies/netflix' },
    { label: 'Apple', path: '/movies/provider-apple' },
    { label: 'Prime Video', path: '/movies/provider-prime-video' },
    { label: 'Hulu', path: '/movies/provider-hulu' },
    { label: 'Disney+', path: '/movies/provider-disney' },
  ]

  const showsDropdown = [
    { label: 'Popular', path: '/shows/popular' },
    { label: 'Top Rated', path: '/shows/top-rated' },
    { label: 'Airing Today', path: '/shows/airing-today' },
    { label: 'On The Air', path: '/shows/on-the-air' },
    { label: 'Marvel', path: '/shows/marvel' },
    { label: 'Netflix', path: '/shows/netflix' },
    { label: 'HBO', path: '/shows/network-hbo' },
    { label: 'Apple', path: '/shows/network-apple' },
    { label: 'ABC', path: '/shows/network-abc' },
    { label: 'FX', path: '/shows/network-fx' },
    { label: 'Prime Video', path: '/shows/network-prime-video' },
    { label: 'Hulu', path: '/shows/network-hulu' },
    { label: 'ESPN', path: '/shows/network-espn' },
    { label: 'Disney+', path: '/shows/provider-disney' },
  ]

  const actorsDropdown = [{ label: 'Popular', path: '/actors/popular' }]

  return (
    <StyledAppBar position='fixed' elevation={0} scrolled={scrolled}>
      <StyledToolbar>
        <MobileMenuArea>
          <MenuIconButton
            onClick={(e) => {
              setMobileMenuAnchor(e.currentTarget)
              setDrawerScreen('root')
            }}
          >
            <MenuIcon />
          </MenuIconButton>
        </MobileMenuArea>

        <MobileLogoOverlay>
          <MobileLogoClickable>
            <LogoWrapper component={RouterLink} to='/'>
              <LogoBox>🎬</LogoBox>
              <LogoText>
                movie<LogoAccent>ception</LogoAccent>
              </LogoText>
            </LogoWrapper>
          </MobileLogoClickable>
        </MobileLogoOverlay>

        <DesktopLeftContainer>
          <LogoWrapper component={RouterLink} to='/'>
            <LogoBox>🎬</LogoBox>
            <LogoText>
              movie<LogoAccent>ception</LogoAccent>
            </LogoText>
          </LogoWrapper>
        </DesktopLeftContainer>

        <DesktopNavContainer>
          {navItems.map((item) => (
            <NavButton key={item.path} component={RouterLink} to={item.path}>
              {item.label}
            </NavButton>
          ))}

          <NavButton
            endIcon={<ArrowDownIcon />}
            onClick={(e) => setMoviesAnchor(e.currentTarget)}
          >
            Movies
          </NavButton>

          <Menu
            anchorEl={moviesAnchor}
            open={Boolean(moviesAnchor)}
            onClose={() => setMoviesAnchor(null)}
          >
            {moviesDropdown.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  navigate(item.path)
                  setMoviesAnchor(null)
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>

          <NavButton
            endIcon={<ArrowDownIcon />}
            onClick={(e) => setShowsAnchor(e.currentTarget)}
          >
            Shows
          </NavButton>

          <Menu
            anchorEl={showsAnchor}
            open={Boolean(showsAnchor)}
            onClose={() => setShowsAnchor(null)}
          >
            {showsDropdown.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  navigate(item.path)
                  setShowsAnchor(null)
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>

          <NavButton
            endIcon={<ArrowDownIcon />}
            onClick={(e) => setActorsAnchor(e.currentTarget)}
          >
            Actors
          </NavButton>

          <Menu
            anchorEl={actorsAnchor}
            open={Boolean(actorsAnchor)}
            onClose={() => setActorsAnchor(null)}
          >
            {actorsDropdown.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  navigate(item.path)
                  setActorsAnchor(null)
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>

          {/* <NavButton onClick={() => navigate('/actors')}>Actors</NavButton> */}
        </DesktopNavContainer>

        <RightContainer>
          <SearchWrapper>
            <SearchIconButton onClick={() => setSearchOpen(true)}>
              <SearchIcon />
            </SearchIconButton>

            {searchOpen && (
              <SearchPanel>
                <SearchRow>
                  <SearchInput
                    autoFocus
                    placeholder='Search...'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />

                  <CloseButton
                    size='small'
                    onClick={() => {
                      setSearchOpen(false)
                      setSearchQuery('')
                    }}
                  >
                    <CloseIcon fontSize='small' />
                  </CloseButton>
                </SearchRow>

                {searchQuery && (
                  <ResultsBox>
                    {loading && <StatusRow>Searching...</StatusRow>}
                    {!loading && results.length === 0 && (
                      <StatusRow>No results</StatusRow>
                    )}
                    {!loading &&
                      results.map((item) => (
                        <ResultItem
                          key={item.id}
                          onClick={() => {
                            navigate(
                              item.type === 'person'
                                ? `/actors/${item.id}`
                                : `/details/${item.type}/${item.id}`,
                            )
                            setSearchOpen(false)
                            setSearchQuery('')
                          }}
                        >
                          {item.posterUrl ? (
                            <SearchThumb src={item.posterUrl} alt='' />
                          ) : (
                            <SearchThumbFallback>
                              {item.title?.charAt(0)}
                            </SearchThumbFallback>
                          )}
                          {item.title}
                        </ResultItem>
                      ))}
                  </ResultsBox>
                )}

                {/* Empty search box = recall mode: offer the titles the user
                    looked at recently instead of a blank panel. */}
                {!searchQuery && recentViews.length > 0 && (
                  <ResultsBox>
                    <StatusRow>Recently viewed</StatusRow>
                    {recentViews.slice(0, 6).map((item) => {
                      const type =
                        item.mediaType.toLowerCase() === 'movie'
                          ? 'movie'
                          : 'show'
                      return (
                        <ResultItem
                          key={`recent-${item.mediaType}-${item.tmdbId}`}
                          onClick={() => {
                            navigate(`/details/${type}/${item.tmdbId}`)
                            setSearchOpen(false)
                            setSearchQuery('')
                          }}
                        >
                          {item.posterPath ? (
                            <SearchThumb src={item.posterPath} alt='' />
                          ) : (
                            <SearchThumbFallback>
                              {item.title?.charAt(0)}
                            </SearchThumbFallback>
                          )}
                          {item.title}
                          {item.releaseYear ? ` (${item.releaseYear})` : ''}
                        </ResultItem>
                      )
                    })}
                  </ResultsBox>
                )}
              </SearchPanel>
            )}
          </SearchWrapper>

          {isLoading ? (
            <CircularProgress size={24} />
          ) : isAuthenticated && user ? (
            <>
              <UserAvatar
                alt={user.name || 'User'}
                onClick={(e) => setAvatarAnchor(e.currentTarget)}
              >
                <LazyImage
                  src={user.profilePictureUrl}
                  alt={user.name || 'User'}
                  fallback={user.name?.charAt(0) || 'U'}
                  variant='circular'
                />
              </UserAvatar>
              <Menu
                anchorEl={avatarAnchor}
                open={Boolean(avatarAnchor)}
                onClose={() => setAvatarAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    navigate('/profile')
                    setAvatarAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <PersonIcon fontSize='small' />
                  </ListItemIcon>
                  Profile
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    navigate('/profile/settings')
                    setAvatarAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize='small' />
                  </ListItemIcon>
                  Settings
                </MenuItem>

                {user.userTypeId === 2 && (
                  <MenuItem
                    onClick={() => {
                      navigate('/admin')
                      setAvatarAnchor(null)
                    }}
                  >
                    <ListItemIcon>
                      <AdminPanelSettingsIcon fontSize='small' />
                    </ListItemIcon>
                    Admin
                  </MenuItem>
                )}

                <Divider />

                <MenuItem
                  onClick={() => {
                    navigate('/about')
                    setAvatarAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <InfoOutlinedIcon fontSize='small' />
                  </ListItemIcon>
                  About
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    navigate('/guide')
                    setAvatarAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <MenuBookOutlinedIcon fontSize='small' />
                  </ListItemIcon>
                  Guide
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    navigate('/help')
                    setAvatarAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <HelpOutlineIcon fontSize='small' />
                  </ListItemIcon>
                  Help
                </MenuItem>

                <Divider />

                <MenuItem
                  onClick={() => {
                    logout()
                    setAvatarAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize='small' />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <DesktopLoggedOutArea>
              <DesktopSignInButton onClick={() => loginWithPopup()}>
                Sign In
              </DesktopSignInButton>

              <DesktopMoreMenuButton
                onClick={(e) => setDesktopMoreAnchor(e.currentTarget)}
                aria-label='More'
              >
                <MenuIcon />
              </DesktopMoreMenuButton>

              <Menu
                anchorEl={desktopMoreAnchor}
                open={Boolean(desktopMoreAnchor)}
                onClose={() => setDesktopMoreAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    navigate('/about')
                    setDesktopMoreAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <InfoOutlinedIcon fontSize='small' />
                  </ListItemIcon>
                  About
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    navigate('/guide')
                    setDesktopMoreAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <MenuBookOutlinedIcon fontSize='small' />
                  </ListItemIcon>
                  Guide
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    navigate('/help')
                    setDesktopMoreAnchor(null)
                  }}
                >
                  <ListItemIcon>
                    <HelpOutlineIcon fontSize='small' />
                  </ListItemIcon>
                  Help
                </MenuItem>
              </Menu>
            </DesktopLoggedOutArea>
          )}
        </RightContainer>

        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={() => {
            setMobileMenuAnchor(null)
            setDrawerScreen('root')
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {drawerScreen === 'movies' && (
            <>
              <MenuItem onClick={() => setDrawerScreen('root')}>
                ← Back
              </MenuItem>

              {moviesDropdown.map((item) => (
                <MenuItem
                  key={item.path}
                  onClick={() => {
                    navigate(item.path)
                    setMobileMenuAnchor(null)
                    setDrawerScreen('root')
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </>
          )}

          {drawerScreen === 'shows' && (
            <>
              <MenuItem onClick={() => setDrawerScreen('root')}>
                ← Back
              </MenuItem>

              {showsDropdown.map((item) => (
                <MenuItem
                  key={item.path}
                  onClick={() => {
                    navigate(item.path)
                    setMobileMenuAnchor(null)
                    setDrawerScreen('root')
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </>
          )}

          <MenuItem
            onClick={() => {
              navigate('/discover')
              setMobileMenuAnchor(null)
            }}
          >
            <ListItemIcon>
              <ExploreOutlinedIcon fontSize='small' />
            </ListItemIcon>
            Discover
          </MenuItem>

          <MenuItem
            onClick={() => {
              navigate('/randomizer')
              setMobileMenuAnchor(null)
            }}
          >
            <ListItemIcon>
              <CasinoOutlinedIcon fontSize='small' />
            </ListItemIcon>
            Randomizer
          </MenuItem>

          <MenuItem
            onClick={() => {
              navigate('/actors/popular')
              setMobileMenuAnchor(null)
            }}
          >
            <ListItemIcon>
              <PeopleOutlineIcon fontSize='small' />
            </ListItemIcon>
            Actors
          </MenuItem>

          <MenuItem
            onClick={(e) => {
              setSubmenuAnchor(e.currentTarget)
              setSubmenuType('movies')
            }}
          >
            <ListItemIcon>
              <MovieOutlinedIcon fontSize='small' />
            </ListItemIcon>
            Movies
            <ChevronRightIcon fontSize='small' sx={{ ml: 'auto' }} />
          </MenuItem>

          <MenuItem
            onClick={(e) => {
              setSubmenuAnchor(e.currentTarget)
              setSubmenuType('shows')
            }}
          >
            <ListItemIcon>
              <LiveTvOutlinedIcon fontSize='small' />
            </ListItemIcon>
            Shows
            <ChevronRightIcon fontSize='small' sx={{ ml: 'auto' }} />
          </MenuItem>

          {!isAuthenticated && [
            <Divider key='more-divider' />,
            <MenuItem
              key='about'
              onClick={() => {
                navigate('/about')
                setMobileMenuAnchor(null)
              }}
            >
              <ListItemIcon>
                <InfoOutlinedIcon fontSize='small' />
              </ListItemIcon>
              About
            </MenuItem>,
            <MenuItem
              key='guide'
              onClick={() => {
                navigate('/guide')
                setMobileMenuAnchor(null)
              }}
            >
              <ListItemIcon>
                <MenuBookOutlinedIcon fontSize='small' />
              </ListItemIcon>
              Guide
            </MenuItem>,
            <MenuItem
              key='help'
              onClick={() => {
                navigate('/help')
                setMobileMenuAnchor(null)
              }}
            >
              <ListItemIcon>
                <HelpOutlineIcon fontSize='small' />
              </ListItemIcon>
              Help
            </MenuItem>,
            <Divider key='signin-divider' />,
            <MenuItem
              key='signin'
              onClick={() => {
                loginWithPopup()
                setMobileMenuAnchor(null)
              }}
            >
              <ListItemIcon>
                <LoginIcon fontSize='small' />
              </ListItemIcon>
              Sign In
            </MenuItem>,
          ]}
        </Menu>

        <Menu
          anchorEl={submenuAnchor}
          open={Boolean(submenuAnchor)}
          onClose={() => {
            setSubmenuAnchor(null)
            setSubmenuType(null)
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {submenuType === 'movies' &&
            moviesDropdown.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  navigate(item.path)
                  setSubmenuAnchor(null)
                  setMobileMenuAnchor(null)
                  setSubmenuType(null)
                }}
              >
                {item.label}
              </MenuItem>
            ))}

          {submenuType === 'shows' &&
            showsDropdown.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  navigate(item.path)
                  setSubmenuAnchor(null)
                  setMobileMenuAnchor(null)
                  setSubmenuType(null)
                }}
              >
                {item.label}
              </MenuItem>
            ))}

          {submenuType === 'actors' &&
            actorsDropdown.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  navigate(item.path)
                  setSubmenuAnchor(null)
                  setMobileMenuAnchor(null)
                  setSubmenuType(null)
                }}
              >
                {item.label}
              </MenuItem>
            ))}
        </Menu>
      </StyledToolbar>
    </StyledAppBar>
  )
}
